/* eslint-disable @typescript-eslint/no-var-requires */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OptimizelyProvider, createInstance } from '@optimizely/react-sdk';
import {
  Frame,
  ProgressBar,
  SageBreakpointProvider,
  SageTokens,
} from '@kajabi/sage-react';
import { Honeybadger } from '@honeybadger-io/react';

import * as StepComponents from './steps';
import LeadGenService from '../../services/lead-gen-service';
import {
  COURSE_ID,
  COACHING_PROGRAM_ID,
  COMMUNITY_ID,
  PODCAST_ID,
  PRODUCT_PROMPTS,
} from '../../utils/constants';
import { buildSignupUrl } from '../../services/utils_service';

declare global {
  interface Window {
    optimizelyDatafile: any;
    LandersTracking: any;
  }
}

const { uuid4 } = require('fast-uuid');
// Instantiate an Optimizely client
const optimizely = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
  datafileOptions: {
    autoUpdate: true,
  },
});

const KAJABI_OFS_USER_ID = 'KAJABI_OFS_USER_ID';

// todo: remove StepsMap once we are hooked up to an optimizely experiment that will provide
// the experiment variation data and subsequently an array of step keys
const StepsMap: Array<string> = [
  'BusinessTopicSlide',
  'ProductPlansSlide',
  'Loading',
  'BusinessPreviewSlide',
];

import './ai_lead_gen.scss';
import { COPY_MAGIC_PROMPTS } from '../../utils/constants';

const AiLeadGen = () => {
  const [currentStep, setCurrentStep] = useState(
    StepsMap.indexOf(
      LeadGenService.retrieveStepData()?.stepKey || 'BusinessTopicSlide'
    )
  );
  const [contentTab] = useState('landing_page');
  const [fetchingAiData, setFetchingAiData] = useState(false);
  const [aiError, setAiError] = useState(false);

  const userId = useMemo(() => {
    const localStorageUUID =
      localStorage.getItem(KAJABI_OFS_USER_ID) || uuid4();
    localStorage.setItem(KAJABI_OFS_USER_ID, localStorageUUID);
    return localStorageUUID;
  }, []);

  const StepComponent = useMemo(() => {
    return StepComponents[StepsMap[currentStep] as keyof typeof StepComponents];
  }, [currentStep]);

  const percentageComplete = useMemo(
    () => ((currentStep + 1) / StepsMap.length) * 100,
    [currentStep]
  );

  const [userData, setUserData] = useState({
    optimizely_id: userId,
    steps_completed: [],
    ...LeadGenService.retrieveStepData()?.data,
  });

  // after each new input on a slide, update the current stored
  // position in localStorage in the event of resuming after abandonment
  useEffect(() => {
    LeadGenService.updateStepProgressData({
      stepKey: StepsMap[currentStep],
      data: userData,
    });
  }, [userData, currentStep]);

  const fetchAiData = useCallback(async (data: any) => {
    setFetchingAiData(true);
    setAiError(false);

    try {
      const {
        business_topic: topic,
        youtube_url: youtubeUrl,
        industry: industry,
        plan_to_sell: planToSell,
      } = data;

      if ((topic || youtubeUrl) && industry && planToSell) {
        const productPrompt =
          PRODUCT_PROMPTS[planToSell as keyof typeof PRODUCT_PROMPTS][
            youtubeUrl ? 'youtube' : 'topic'
          ];

        const landingPagePrompt = youtubeUrl
          ? COPY_MAGIC_PROMPTS.landingPageFromYoutube
          : COPY_MAGIC_PROMPTS.landingPageFromTopic;

        const salesEmailPrompt = youtubeUrl
          ? COPY_MAGIC_PROMPTS.salesEmailFromYoutube
          : COPY_MAGIC_PROMPTS.salesEmailFromTopic;

        const checkoutPagePrompt = youtubeUrl
          ? COPY_MAGIC_PROMPTS.checkoutPageFromYoutube
          : COPY_MAGIC_PROMPTS.checkoutPageFromTopic;

        const productResponse = await LeadGenService.createAiData({
          topic: topic,
          youtube_url: youtubeUrl,
          industry: industry,
          prompt_name: productPrompt,
          optimizely_id: userId,
        });

        const landingPageResponse = await LeadGenService.createAiData({
          topic: topic,
          youtube_url: youtubeUrl,
          industry: industry,
          prompt_name: landingPagePrompt,
          optimizely_id: userId,
        });

        const salesEmailResponse = await LeadGenService.createAiData({
          topic: topic,
          youtube_url: youtubeUrl,
          industry: industry,
          prompt_name: salesEmailPrompt,
          optimizely_id: userId,
        });

        const checkoutPageResponse = await LeadGenService.createAiData({
          topic: topic,
          youtube_url: youtubeUrl,
          industry: industry,
          prompt_name: checkoutPagePrompt,
          optimizely_id: userId,
        });

        if (
          productResponse.success &&
          landingPageResponse.success &&
          salesEmailResponse.success &&
          checkoutPageResponse.success
        ) {
          let businessTopic = '';

          if (topic) {
            businessTopic = topic;
          } else if (productResponse.data.business_description) {
            businessTopic = productResponse.data.business_description;
          }

          const aiData = {
            coaching_programs:
              planToSell === COACHING_PROGRAM_ID ? productResponse.data : null,
            communities:
              planToSell === COMMUNITY_ID ? productResponse.data : null,
            courses: planToSell === COURSE_ID ? productResponse.data : null,
            podcasts: planToSell === PODCAST_ID ? productResponse.data : null,
            landing_pages: landingPageResponse.data,
            emails: salesEmailResponse.data,
            checkouts: checkoutPageResponse.data,
          };

          setUserData((prev: object) => ({
            ...prev,
            business_topic: businessTopic,
            ai_data: aiData,
          }));
        } else {
          setAiError(true);
        }
      }
    } catch (e: any) {
      Honeybadger.notify(e);
      setAiError(true);
    } finally {
      setFetchingAiData(false);
    }
  }, []);

  const handleStepChange = useCallback(async () => {
    if (
      StepComponent === StepComponents['Loading'] &&
      (fetchingAiData || aiError || userData.ai_data == undefined)
    ) {
      // OpenAI Failure
      window.location.href = buildSignupUrl(userData, true);
      return;
    }

    if (StepComponent === StepComponents['ProductPlansSlide']) {
      fetchAiData(userData);
    }
    userData.steps_completed = [
      ...userData.steps_completed,
      StepsMap[currentStep],
    ];
    setUserData({ ...userData });
    const stepData = {
      steps_completed: userData.steps_completed,
      ...userData,
    };

    try {
      LeadGenService.submitStepData(stepData);
    } catch (e: any) {
      Honeybadger.notify(e);
    }

    // Do not advance to the next step if on the final slide.
    if (StepComponent !== StepComponents['BusinessPreviewSlide']) {
      setCurrentStep((s: number) => s + 1);
    }
  }, [currentStep, fetchAiData, StepComponent, userData]);

  const handleOnChange = useCallback((event?: any) => {
    setUserData((prevData: any) => ({
      ...prevData,
      [event.target.id]: event.target.value,
    }));
  }, []);

  useEffect(() => {
    optimizely.activate('ai_lead_gen_main_test', userId);
  }, [userId]);

  return (
    <OptimizelyProvider optimizely={optimizely} user={{ id: userId }}>
      <SageBreakpointProvider queries={SageTokens.BREAKPOINT_QUERIES}>
        <Frame
          gap={Frame.GAPS.NONE}
          style={{
            position: 'relative',
            height:
              StepComponent === StepComponents['Loading'] ? '100%' : undefined,
          }}
        >
          {/* Progress Container */}
          {StepComponent !== StepComponents['BusinessPreviewSlide'] && (
            <Frame gap={Frame.GAPS.NONE}>
              <Frame
                gap={Frame.GAPS.NONE}
                align={Frame.ALIGNMENTS.CENTER_SPREAD}
                direction={Frame.DIRECTIONS.HORIZONTAL}
              >
                <img
                  alt="Kajabi Logo"
                  src="https://kajabi-app-assets.kajabi-cdn.com/ai-lead-gen/Kajabi.png"
                  style={{ height: 80, padding: 20 }}
                ></img>
              </Frame>
              <ProgressBar
                color={SageTokens.COLOR_PALETTE.CHARCOAL_500}
                style={{ width: '100%' }}
                percent={percentageComplete}
                disableTooltip
              />
            </Frame>
          )}
          <StepComponent
            contentTab={contentTab}
            handleOnChange={handleOnChange}
            onStepChange={handleStepChange}
            userData={userData}
          />
        </Frame>
      </SageBreakpointProvider>
    </OptimizelyProvider>
  );
};

export default AiLeadGen;
